export type DashboardFieldKind = 'category' | 'text' | 'number' | 'timestamp'

export type DashboardFieldSchema = {
  readonly label: string
  readonly kind: DashboardFieldKind
  readonly operators: readonly string[]
  readonly options: Readonly<Record<string, string>> | null
}

export type DashboardContract = {
  readonly id: string
  readonly searchMinimumLength: number | null
  readonly fields: Readonly<Record<string, DashboardFieldSchema>>
}

export function defineDashboard<const Contract extends DashboardContract>(contract: Contract) {
  return contract
}

/** A length of time, as the relative and age operators take it. */
export type Period = { amount: number; unit: 'hours' | 'days' }

export type FieldId<Contract extends DashboardContract> = keyof Contract['fields'] & string

type CategoryValue<Field> = Field extends { readonly options: infer Options }
  ? Options extends Readonly<Record<string, string>> ? keyof Options & string : string
  : string

/** One filter on a field, written as `{ operator: value }`. The field's kind decides which operators exist. */
export type FieldFilterValue<Field extends DashboardFieldSchema> =
  Field['kind'] extends 'category' ? { in: readonly CategoryValue<Field>[] } | { empty: boolean }
    : Field['kind'] extends 'text'
      ? { equals: string } | { contains: string } | { startsWith: string } | { endsWith: string } | { empty: boolean }
      : Field['kind'] extends 'number'
        ? { equals: number } | { atLeast: number } | { atMost: number } | { between: { from: number; to: number } } | { empty: boolean }
        : { before: string } | { after: string } | { between: { from: string; to: string } }
          | { relative: Period } | { age: { min?: Period; max?: Period } } | { empty: boolean }

/** Filters to set; `undefined` clears a field. */
export type DashboardFilterPatch<Contract extends DashboardContract> = {
  [Id in FieldId<Contract>]?: FieldFilterValue<Contract['fields'][Id]> | undefined
}

/** The wire shape the server validates against the dashboard definition. */
export type FieldFilter = { field: string; operator: string; values: unknown }

/** `highlight` names fields whose filter the chart that set it draws as a selection instead of applying. */
export type FilterCriteria = { search?: string; filters?: FieldFilter[]; highlight?: string[] }

export function toFieldFilter(field: string, value: object): FieldFilter {
  const [operator, values] = Object.entries(value)[0]!
  return { field, operator, values }
}

export function fromFieldFilter(filter: FieldFilter): object {
  return { [filter.operator]: filter.values }
}
